import { useFlushOnLeave } from '@src/hooks/useFlushOnLeave';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { QuoteCardsEditorStore } from '@src/editor/QuoteCardsEditorStore';
import type { AutosaveStatus } from '@src/hooks/useAutosave';
import type { Renderer } from '@src/renderer/Renderer';
import type { StoryRepository } from '@src/repositories';
import type { Artifact, Story } from '@src/types.ts/story';
import type { MutableRefObject } from 'react';

const logger = LogManager.getLogger('hook:useQuoteCardAutoSave');

const AUTO_SAVE_DEBOUNCE_MS = 2000;

export interface UseQuoteCardAutoSaveParams {
  editorStoreRef: MutableRefObject<QuoteCardsEditorStore | null>;
  rendererRef: MutableRefObject<Renderer | null>;
  compositionReady: boolean;
  isSaved: boolean;
  story: Story | undefined | null;
  saveParams: {
    id: string;
    quote: string;
    imagefile?: Artifact;
    speakerId?: string;
    name: string;
    org: string;
    currentEditor: string;
  };
  activeTemplateId: string;
  metadata: Record<string, any> | null | undefined;
  storyRepository: StoryRepository | null | undefined;
  /** Fires after the very first autosave persists an unsaved card. */
  onFirstSave?: (
    newId: string,
    source: Record<string, any>,
    templateId: string,
  ) => void;
  /** Fires after any subsequent autosave (once the card is already saved). */
  onAutoSaved?: (source: Record<string, any>, templateId: string) => void;
}

export interface UseQuoteCardAutoSaveResult {
  status: AutosaveStatus;
  lastSavedAt: Date | null;
  /** Immediately persist any pending dirty changes. No-ops if not dirty. */
  flush: () => Promise<void>;
  /** Cancel any pending debounced save and reset dirty tracking. */
  cancel: () => void;
  /** Schedule a save with no editor change (e.g. the card moved template). */
  requestSave: () => void;
}

export function useQuoteCardAutoSave({
  editorStoreRef,
  rendererRef,
  compositionReady,
  isSaved,
  story,
  saveParams,
  activeTemplateId,
  metadata,
  storyRepository,
  onFirstSave,
  onAutoSaved,
}: UseQuoteCardAutoSaveParams): UseQuoteCardAutoSaveResult {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const isDirtyRef = useRef(false);
  const inFlightRef = useRef(false);

  const activeTemplateIdRef = useRef(activeTemplateId);
  useEffect(() => {
    activeTemplateIdRef.current = activeTemplateId;
  }, [activeTemplateId]);

  const metadataRef = useRef(metadata);
  useEffect(() => {
    metadataRef.current = metadata;
  }, [metadata]);

  const saveParamsRef = useRef(saveParams);
  useEffect(() => {
    saveParamsRef.current = saveParams;
  }, [saveParams]);

  const isSavedRef = useRef(isSaved);
  useEffect(() => {
    isSavedRef.current = isSaved;
  }, [isSaved]);

  const storyRef = useRef(story);
  useEffect(() => {
    storyRef.current = story;
  }, [story]);

  const onFirstSaveRef = useRef(onFirstSave);
  useEffect(() => {
    onFirstSaveRef.current = onFirstSave;
  }, [onFirstSave]);

  const onAutoSavedRef = useRef(onAutoSaved);
  useEffect(() => {
    onAutoSavedRef.current = onAutoSaved;
  }, [onAutoSaved]);

  const runSave = useCallback(async () => {
    if (!isDirtyRef.current || !rendererRef.current || !storyRepository) return;
    if (inFlightRef.current) return;

    const source = rendererRef.current.getSource() || {};
    const params = saveParamsRef.current;
    const formattedStoryteller = `${params.name}|${params.org}`;
    const templateId = activeTemplateIdRef.current;
    const saveMetadata = {
      ...(metadataRef.current || {}),
      editedBy: params.currentEditor,
    };

    inFlightRef.current = true;
    setStatus('saving');

    try {
      if (!isSavedRef.current) {
        const currentStory = storyRef.current;
        if (!currentStory) {
          throw new Error('Cannot create shareable image without a story');
        }
        const newId = await storyRepository.createShareableImage(
          currentStory,
          params.quote,
          params.imagefile,
          formattedStoryteller,
          source,
          templateId,
          saveMetadata,
          params.speakerId,
        );
        isDirtyRef.current = false;
        setLastSavedAt(new Date());
        setStatus('saved');
        onFirstSaveRef.current?.(newId, source, templateId);
      } else {
        await storyRepository.updateShareableImage(
          params.id,
          params.quote,
          params.imagefile,
          formattedStoryteller,
          source,
          templateId,
          saveMetadata,
          params.speakerId,
        );
        isDirtyRef.current = false;
        setLastSavedAt(new Date());
        setStatus('saved');
        onAutoSavedRef.current?.(source, templateId);
      }
    } catch (err) {
      logger.error('Autosave failed', extractErrorInfo(err));
      setStatus('error');
    } finally {
      inFlightRef.current = false;
    }
  }, [rendererRef, storyRepository]);

  const debouncedAutosave = useMemo(
    () => debounce(runSave, AUTO_SAVE_DEBOUNCE_MS),
    [runSave],
  );

  useEffect(() => {
    if (!editorStoreRef.current || !compositionReady) return;

    const observer = {
      onEditorChange: () => {
        isDirtyRef.current = true;
        setStatus('idle');
        debouncedAutosave();
      },
    };
    editorStoreRef.current.editor.registerObserver(observer);

    return () => {
      debouncedAutosave.cancel();
      editorStoreRef.current?.editor.unregisterObserver(observer);
    };
  }, [compositionReady, debouncedAutosave, editorStoreRef]);

  const flush = useCallback(async () => {
    debouncedAutosave.cancel();
    if (!isDirtyRef.current) return;
    await runSave();
  }, [debouncedAutosave, runSave]);

  const cancel = useCallback(() => {
    debouncedAutosave.cancel();
    isDirtyRef.current = false;
  }, [debouncedAutosave]);

  const requestSave = useCallback(() => {
    isDirtyRef.current = true;
    setStatus('idle');
    debouncedAutosave();
  }, [debouncedAutosave]);

  useFlushOnLeave(flush, () => isDirtyRef.current);

  return { status, lastSavedAt, flush, cancel, requestSave };
}
